import type { ViewRecordFieldChange } from '@salescore/client-redux'
import type { ViewQueryNode, ViewQueryRecordNode, ViewQueryRecordNodeWithId } from '@salescore/core'
import { Array, pipe, Result } from 'effect'

import type { DeepReadonly } from '../../types/DeepReadonly'
import { addNewChildRecord, type AddNewChildRecordLeft } from './addNewChildRecord'
import { addNewRootRecord } from './addNewRootRecord'

export interface NewRecordChange {
  rowIndex: number
  // 挿入位置: recordNodes 内の挿入 index と新規レコードの meta レンジ生成に使う。
  innerRowIndex: number
  // 親選択キー: ユーザーが選択したセルの生 innerRowIndex。
  // 挿入位置 (直前兄弟の排他境界値) で親を引くと半開区間判定で隣接親に紐付くため、親特定には挿入位置ではなくこちらを使う。
  parentSelectionInnerRowIndex: number
  node: ViewQueryNode
  height: number
}

export type OnAfterChange = (changes: ViewRecordFieldChange[]) => void

type AddNewRecordRight =
  | {
      kind: 'root'
      recordNode: ViewQueryRecordNodeWithId
      change: ViewRecordFieldChange | undefined
    }
  | {
      kind: 'child'
      recordNode: ViewQueryRecordNode
      rootRecordNode: ViewQueryRecordNodeWithId
      change: ViewRecordFieldChange | undefined
    }

export const addNewRecords = (
  initRootNodes: DeepReadonly<ViewQueryRecordNode[]>,
  initRecordIds: DeepReadonly<string[]>,
  newRecordChanges: DeepReadonly<NewRecordChange[]>,
): {
  rootNodes: DeepReadonly<ViewQueryRecordNode[]>
  recordIds: DeepReadonly<string[]>
  results: Array<Result.Result<AddNewRecordRight, AddNewChildRecordLeft>>
} =>
  Array.reduce(
    newRecordChanges,
    {
      rootNodes: initRootNodes,
      recordIds: initRecordIds,
      results: [] as Array<Result.Result<AddNewRecordRight, AddNewChildRecordLeft>>,
    },
    (accumulator, newRecordChange) => {
      const { node } = newRecordChange
      if (node.path.length === 1) {
        const result = addNewRootRecord(accumulator.rootNodes, accumulator.recordIds, newRecordChange)
        return {
          rootNodes: result.processedRootRecordNodes,
          recordIds: result.processedNewRecordIds,
          results: [...accumulator.results, Result.succeed({ ...result, kind: 'root' as const })],
        }
      }
      return pipe(
        addNewChildRecord(accumulator.rootNodes, newRecordChange),
        Result.match({
          onFailure: (left) => ({
            rootNodes: accumulator.rootNodes,
            recordIds: accumulator.recordIds,
            results: [...accumulator.results, Result.fail(left)],
          }),
          onSuccess: (right) => ({
            rootNodes: accumulator.rootNodes.toSpliced(newRecordChange.rowIndex, 1, right.rootRecordNode),
            recordIds: accumulator.recordIds,
            results: [...accumulator.results, Result.succeed({ ...right, kind: 'child' as const })],
          }),
        }),
      )
    },
  )
